import * as tslib_1 from "tslib";
import { MemoryFetchCache } from './MemoryFetchCache';
/**
 * Class that wraps fetch() to provide an easy to read API for reading and writing JSON objects over HTTP(S).
 *
 * Constructor accepts:
 *      Default init used for all requests that can be passed to the constructor (e.g. for authetnication by passing 'Authorization': `Bearer ${jwtToken}` in the headers.)
 *      Cache used to look up the value before a request, and to store the value following the requests.  Default is to use a MemoryFetchCache instance only valid for the life of
 *      this object.
 *
 * Returned values have aready had json() called on them.  If we get an error making the request we throw an exception so the return value can awlways be
 * expected to be the decoded json if an exception wasn't thrown.
 */
var ApiFetch = /** @class */ (function () {
    /**
     * Constructor.
     * @param init Init applied to all requests before applying any passed in init for the request.
     * @param cache Cache used to look up the value before a request, and to store the value following the requests.
     */
    function ApiFetch(init, cache) {
        // Set defaultInit to our built in defaults combined with the passed in init.
        this.defaultInit = this.combineRequestInit({
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        }, init);
        // Set the cache.
        this.cache = cache || new MemoryFetchCache();
    }
    /**
     * Combine RequestInit objects together to form a final RequestInit object for a request.
     * @param oldInit
     * @param newInit
     */
    ApiFetch.prototype.combineRequestInit = function () {
        var inits = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            inits[_i] = arguments[_i];
        }
        var ret = {};
        for (var _a = 0, inits_1 = inits; _a < inits_1.length; _a++) {
            var init = inits_1[_a];
            if (!init) {
                continue;
            }
            if (typeof init === 'function') {
                ret = init(ret);
            }
            else {
                var headers = init.headers, rest = tslib_1.__rest(init, ["headers"]);
                ret = tslib_1.__assign({}, ret, rest, { headers: !headers ? ret.headers : tslib_1.__assign({}, ret.headers, headers) });
            }
        }
        return ret;
    };
    /**
     * Fetch json from an API using HTTP GET.
     * @param input
     * @param init
     */
    ApiFetch.prototype.get = function (input, init, cacheOptions) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            return tslib_1.__generator(this, function (_a) {
                return [2 /*return*/, this.fetch(input, tslib_1.__assign({ method: 'GET' }, (init ? init : {})), cacheOptions)];
            });
        });
    };
    /**
     * Fetch json from an API using HTTP POST passing body as json encoded body.
     * @param input
     * @param body
     * @param init
     */
    ApiFetch.prototype.post = function (input, body, init, cacheOptions) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            return tslib_1.__generator(this, function (_a) {
                return [2 /*return*/, this.fetch(input, tslib_1.__assign({ method: 'POST', body: body ? JSON.stringify(body) : undefined }, (init ? init : {})), cacheOptions)];
            });
        });
    };
    /**
     * Fetch json from an API using HTTP PUT passing body as json encoded body.
     * @param input
     * @param body
     * @param init
     */
    ApiFetch.prototype.put = function (input, body, init, cacheOptions) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            return tslib_1.__generator(this, function (_a) {
                return [2 /*return*/, this.fetch(input, tslib_1.__assign({ method: 'PUT', body: body ? JSON.stringify(body) : undefined }, (init ? init : {})), cacheOptions)];
            });
        });
    };
    /**
     * Fetch json from an API using HTTP PATCH passing body as json encoded body.
     * @param input
     * @param body
     * @param init
     */
    ApiFetch.prototype.patch = function (input, body, init, cacheOptions) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            return tslib_1.__generator(this, function (_a) {
                return [2 /*return*/, this.fetch(input, tslib_1.__assign({ method: 'PATCH', body: body ? JSON.stringify(body) : undefined }, (init ? init : {})), cacheOptions)];
            });
        });
    };
    /**
     * Fetch json from an API using HTTP DELETE passing body as json encoded body.
     * @param input
     * @param body
     * @param init
     */
    ApiFetch.prototype.delete = function (input, body, init, cacheOptions) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            return tslib_1.__generator(this, function (_a) {
                return [2 /*return*/, this.fetch(input, tslib_1.__assign({ method: 'DELETE', body: body ? JSON.stringify(body) : undefined }, (init ? init : {})), cacheOptions)];
            });
        });
    };
    /**
    * Wrapper around fetch() that supports jwt tokens, caching, and default urls as configured by setApiFetchDefaultOptions() and the passed in apiOptions.
    *
    * Instead of returning the response as fetch() would, response.json() is called and the result of that returned (cast as a T via Promise<T>).
    *
    * If there is an error or the response as not OK we will throw an exception.
    * @param input
    * @param init
    * @param apiOptions
    */
    ApiFetch.prototype.fetch = function (input /*RequestInfo*/, init, cacheOptions) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var finalInit, cachedResult, response, result_1, anotherError_1, result, error_1;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        finalInit = this.combineRequestInit(this.defaultInit, init);
                        // Check the cache for an existing entry.
                        // NOTE we only use the cache if cacheOptions is passed, otherwise we never check or store a request in the cache.
                        if (cacheOptions && this.cache) {
                            cachedResult = this.cache.readFromCache(input, finalInit);
                            if (cachedResult) {
                                return [2 /*return*/, cachedResult];
                            }
                        }
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 9, , 10]);
                        return [4 /*yield*/, fetch(input, finalInit)];
                    case 2:
                        response = _a.sent();
                        if (!!response.ok) return [3 /*break*/, 7];
                        _a.label = 3;
                    case 3:
                        _a.trys.push([3, 5, , 6]);
                        return [4 /*yield*/, response.json()];
                    case 4:
                        result_1 = _a.sent();
                        return [3 /*break*/, 6];
                    case 5:
                        anotherError_1 = _a.sent();
                        throw "Server indicated an error by returning a status of " + response.status;
                    case 6:
                        if (result_1.error) {
                            if (result_1.error.description) {
                                throw new Error(result_1.error.description);
                            }
                            throw new Error(result_1.error);
                        }
                        else if (result_1.errorMessage) {
                            throw new Error(result_1.errorMessage);
                        }
                        else {
                            throw "Server indicated an error by returning a status of " + response.status;
                        }
                        _a.label = 7;
                    case 7:
                        // Special case for those parts of rest apis that return no body content (.e.g. saving successfully with a POST).
                        if (response.headers.get('Content-Length') === '0') {
                            return [2 /*return*/, null];
                        }
                        return [4 /*yield*/, response.json()];
                    case 8:
                        result = _a.sent();
                        // Store the result in the cache if we can.
                        if (cacheOptions && this.cache) {
                            this.cache.storeInCache(input, finalInit, result, cacheOptions);
                        }
                        return [2 /*return*/, result];
                    case 9:
                        error_1 = _a.sent();
                        throw error_1;
                    case 10: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Raise expire events in the cache.
     * @param defaults
     */
    ApiFetch.prototype.raiseExpireEvents = function () {
        var _a;
        var events = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            events[_i] = arguments[_i];
        }
        if (!this.cache) {
            return;
        }
        (_a = this.cache).raiseExpireEvents.apply(_a, events);
    };
    return ApiFetch;
}());
export { ApiFetch };
//# sourceMappingURL=ApiFetch.js.map